import type { GlobalState } from '@affine/core/modules/storage';
import {
  storeGlobalPreference,
  watchGlobalPreference,
} from '@affine/core/modules/streamlain/shared/global-preference';
import {
  STREAMLAIN_AI_ISLAND_DEFAULT_SNAP_POINT,
  STREAMLAIN_AI_ISLAND_INSET,
  STREAMLAIN_AI_ISLAND_SIZE,
  STREAMLAIN_AI_ISLAND_SNAP_POINTS,
  type StreamlainAIIslandSnapPoint,
} from '@affine/core/modules/streamlain/shared/workspace-pane-layout';

const SNAP_POINT_KEY = 'streamlain.aiIsland.snapPoint';

/** A stored snap point; the default for none or an unknown value. */
export function parseAIIslandSnapPoint(
  value: unknown
): StreamlainAIIslandSnapPoint {
  return (
    STREAMLAIN_AI_ISLAND_SNAP_POINTS.find(point => point === value) ??
    STREAMLAIN_AI_ISLAND_DEFAULT_SNAP_POINT
  );
}

/** The user's snap point for the island (the bottom centre until they move it). */
export function aiIslandSnapPoint$(globalState: GlobalState) {
  return watchGlobalPreference(
    globalState,
    SNAP_POINT_KEY,
    parseAIIslandSnapPoint
  );
}

export function storeAIIslandSnapPoint(
  globalState: GlobalState,
  snap: StreamlainAIIslandSnapPoint
) {
  storeGlobalPreference(globalState, SNAP_POINT_KEY, snap);
}

/** The island's centre at a snap point, measured from the left of the view's bottom row. */
export function aiIslandSnapCentre(
  snap: StreamlainAIIslandSnapPoint,
  rowWidth: number
) {
  const cornerCentre =
    STREAMLAIN_AI_ISLAND_INSET + STREAMLAIN_AI_ISLAND_SIZE / 2;
  switch (snap) {
    case 'bottom-left':
      return cornerCentre;
    case 'bottom-center':
      return rowWidth / 2;
    case 'bottom-right':
      return rowWidth - cornerCentre;
  }
}

/** The snap point nearest to an island centred at `x` on the view's bottom row. */
export function nearestAIIslandSnapPoint(
  x: number,
  rowWidth: number
): StreamlainAIIslandSnapPoint {
  let nearest: StreamlainAIIslandSnapPoint =
    STREAMLAIN_AI_ISLAND_SNAP_POINTS[0];
  for (const snap of STREAMLAIN_AI_ISLAND_SNAP_POINTS) {
    if (
      Math.abs(aiIslandSnapCentre(snap, rowWidth) - x) <
      Math.abs(aiIslandSnapCentre(nearest, rowWidth) - x)
    ) {
      nearest = snap;
    }
  }
  return nearest;
}
